import { useState, type ReactElement, type ReactNode } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { toast } from '../../lib/toast';

export interface ConfirmDialogProps {
  /** Element rendered as the open trigger. Cloned through Radix `asChild`. */
  trigger: ReactNode;
  /** Title displayed at the top of the modal. */
  title: string;
  /** Optional supporting copy rendered under the title. */
  description?: ReactNode;
  /** Label of the confirm button. Defaults to `"Confirmar"`. */
  confirmLabel?: string;
  /** Label of the cancel button. Defaults to `"Cancelar"`. */
  cancelLabel?: string;
  /** When true, the confirm button uses the danger variant. */
  destructive?: boolean;
  /**
   * Confirm handler. May be synchronous or return a Promise.
   *
   * - Resolves successfully → modal closes.
   * - Throws / rejects     → modal stays open and an error toast is shown.
   */
  onConfirm: () => void | Promise<void>;
}

// TODO: refactor para usar <Button> e <Spinner> quando TASK-015 estabilizar
// (decoupling intencional para a TASK-016 não bloquear caso TASK-015 atrase).
const baseButtonClass =
  'inline-flex h-10 items-center justify-center gap-2 rounded-card px-4 text-sm font-medium ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-bg ' +
  'disabled:cursor-not-allowed disabled:opacity-50 select-none';

const cancelButtonClass = cn(
  baseButtonClass,
  'bg-bg-card text-white border border-border hover:bg-bg-hover active:bg-bg-hover/80',
);

const confirmButtonClass = (destructive: boolean): string =>
  cn(
    baseButtonClass,
    destructive
      ? 'bg-danger text-white hover:bg-danger/90 active:bg-danger/80'
      : 'bg-primary text-white hover:bg-primary-hover active:bg-primary-hover/90',
  );

/**
 * Inline spinner used while `onConfirm` is in flight.
 *
 * Mirrors the look of `components/ui/Spinner` to keep visual parity
 * once the refactor TODO above is taken on.
 */
function InlineSpinner(): ReactElement {
  return (
    <span
      role="status"
      aria-label="Carregando"
      className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
  );
}

/**
 * Reusable confirmation modal for destructive (or just consequential) actions.
 *
 * Single component for every confirmation flow in the admin panel
 * (DESIGN seção 9.1, RF18). Wraps Radix Dialog directly — the `Dialog`
 * primitive in `components/ui/Dialog.tsx` (TASK-015) is intentionally
 * not used yet to avoid coupling parallel tasks; once both stabilize
 * the implementation can collapse to that primitive.
 *
 * Behavior contract:
 * - The provided `trigger` opens the modal (cloned via Radix `asChild`).
 * - Esc / overlay click cancels (Radix-native).
 * - During an async `onConfirm`, the confirm button shows a spinner and
 *   the cancel button is disabled.
 * - On success the modal closes; on failure it stays open and the
 *   error message is surfaced via `toast.error`.
 */
export function ConfirmDialog({
  trigger,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  destructive = false,
  onConfirm,
}: ConfirmDialogProps): ReactElement {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async (): Promise<void> => {
    if (loading) return;
    setLoading(true);
    try {
      await onConfirm();
      setOpen(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Falha ao executar ação';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenChange = (next: boolean): void => {
    // Block Esc / overlay clicks from closing the modal mid-flight.
    if (loading && !next) return;
    setOpen(next);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay
          className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm data-[state=open]:animate-fade-in"
        />
        <Dialog.Content
          aria-describedby={description ? 'confirm-dialog-description' : undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-card border border-border bg-bg-card p-6 shadow-2xl',
            'focus:outline-none data-[state=open]:animate-slide-up',
          )}
        >
          <Dialog.Close
            // Hidden when loading to prevent dismissal mid-action.
            disabled={loading}
            className={cn(
              'absolute right-4 top-4 text-muted transition-colors hover:text-white',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
              'rounded-card',
              loading && 'pointer-events-none opacity-50',
            )}
            aria-label="Fechar"
          >
            <X size={18} />
          </Dialog.Close>

          <Dialog.Title className="pr-8 text-lg font-semibold text-white">
            {title}
          </Dialog.Title>

          {description ? (
            <Dialog.Description
              id="confirm-dialog-description"
              className="mt-2 text-sm text-muted"
            >
              {description}
            </Dialog.Description>
          ) : null}

          <div className="mt-6 flex justify-end gap-2">
            <Dialog.Close asChild>
              <button type="button" disabled={loading} className={cancelButtonClass}>
                {cancelLabel}
              </button>
            </Dialog.Close>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={loading}
              aria-busy={loading || undefined}
              className={confirmButtonClass(destructive)}
            >
              {loading ? <InlineSpinner /> : null}
              {confirmLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
